import {
  AlertTriangle,
  Braces,
  Calendar,
  CheckCircle2,
  Copy,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { InputGroup } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { TextareaGroup } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";
import type { Language, SchemaVersionStatus } from "@/types";

// ─────────────────────────────────────────────
// SchemasList — Screen 7 (SrQtK)
//
// Active schema card + schema versions table.
// Design: §8.5 Screen 7
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type SchemaType =
  | "SINGLE_STANDARD"
  | "MULTI_STANDARD"
  | "CUSTOM"
  | "INTERNAL_POLICIES";

interface SchemaVersion {
  id: string;
  schemaId: string;
  version: string;
  status: SchemaVersionStatus;
  type: SchemaType;
  displayVersion?: string | null;
  standardsCovered?: string[];
  questionCount?: number;
  createdBy?: string;
  createdAt: string;
  publishedAt?: string;
}

interface Schema {
  id: string;
  name: string;
  description?: string;
  isPreset?: boolean;
  latestVersion?: SchemaVersion;
  publishedVersion?: SchemaVersion;
  versions: SchemaVersion[];
  createdAt: string;
}

interface SchemasResponse {
  data: Schema[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Schema type badge ─────────────────────────

const SCHEMA_TYPE_LABELS: Record<SchemaType, string> = {
  SINGLE_STANDARD: "Single Standard",
  MULTI_STANDARD: "Multi Standard",
  CUSTOM: "Custom",
  INTERNAL_POLICIES: "Internal Policies",
};

function SchemaTypeBadge({ type }: { type: SchemaType }) {
  return (
    <span className="inline-flex items-center rounded-badge bg-accent-bg px-2 py-0.5 text-[12px] font-semibold leading-none text-accent-text">
      {SCHEMA_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ── Active schema card ────────────────────────

function ActiveSchemaCard({
  schema,
  onView,
}: {
  schema: Schema;
  onView: () => void;
}) {
  const { t, language } = useTranslation();
  const pv = schema.publishedVersion;
  if (!pv) return null;

  return (
    <div
      className="rounded-card border border-success bg-success-bg p-5"
      style={{ borderColor: "var(--success)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-success" />
            <span className="text-[12px] font-semibold uppercase tracking-[1.5px] text-success">
              {t("schema.activeSchema")}
            </span>
          </div>
          <h2 className="text-[18px] font-semibold text-text-primary">
            {schema.name}
          </h2>
          <div className="flex items-center gap-3">
            <StatusBadge type="schemaVersion" status="PUBLISHED" />
            <span className="font-mono text-caption text-text-muted">
              v{pv.version}
            </span>
            {pv.publishedAt && (
              <span className="flex items-center gap-1 text-caption text-text-muted">
                <Calendar className="h-3 w-3" />
                {formatDate(pv.publishedAt, language)}
              </span>
            )}
            {pv.createdBy && (
              <span className="text-caption text-text-muted">
                by {pv.createdBy}
              </span>
            )}
          </div>
        </div>
        <Button variant="outline" onClick={onView}>
          {t("schema.viewSchema")}
        </Button>
      </div>
    </div>
  );
}

// ── Table columns ─────────────────────────────

function buildColumns(
  navigate: ReturnType<typeof useNavigate>,
  onDelete: (schema: Schema) => void,
  onCopy: (schema: Schema) => void,
  t: (key: string) => string,
  language: Language
): ColumnDef<Schema>[] {
  return [
    {
      key: "name",
      header: t("schema.schemaName"),
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-primary">{row.name}</span>
          {row.isPreset && (
            <span className="inline-flex items-center rounded-badge bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-text">
              Preset
            </span>
          )}
        </div>
      ),
    },
    {
      key: "type",
      header: t("schema.type"),
      cell: (row) =>
        row.latestVersion ? (
          <SchemaTypeBadge type={row.latestVersion.type} />
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "version",
      header: t("schema.version"),
      cell: (row) => {
        const v = row.latestVersion;
        const label = v?.displayVersion?.trim()
          ? v.displayVersion.trim()
          : v
          ? `v${v.version}`
          : null;
        return (
          <span className="font-mono text-caption text-text-muted">
            {label ?? "-"}
          </span>
        );
      },
    },
    {
      key: "status",
      header: t("schema.status"),
      cell: (row) =>
        row.latestVersion ? (
          <StatusBadge
            type="schemaVersion"
            status={row.latestVersion.status}
          />
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "standards",
      header: t("schema.standards"),
      cell: (row) => {
        const standards = row.latestVersion?.standardsCovered ?? [];
        if (standards.length === 0) return <span className="text-text-muted">-</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {standards.slice(0, 3).map((s) => (
              <span
                key={s}
                className="rounded-badge bg-hover px-1.5 py-0.5 text-[11px] font-medium text-text-muted"
              >
                {s}
              </span>
            ))}
            {standards.length > 3 && (
              <span className="text-caption text-text-muted">
                +{standards.length - 3}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "questions",
      header: t("schema.questions"),
      cell: (row) => (
        <span className="text-text-secondary">
          {row.latestVersion?.questionCount ?? "-"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: t("common.created"),
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatDate(row.createdAt, language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            title="Duplicate"
            className="flex h-7 w-7 items-center justify-center rounded-control text-text-muted transition-colors hover:bg-hover hover:text-text-secondary"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(row);
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          {!row.isPreset && (
            <button
              type="button"
              title="Delete"
              className="flex h-7 w-7 items-center justify-center rounded-control text-text-muted transition-colors hover:bg-danger-bg hover:text-danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];
}

// ── Create Schema Dialog ──────────────────────

interface CreateSchemaDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}

function CreateSchemaDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateSchemaDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<SchemaType | "">("");
  const [displayVersion, setDisplayVersion] = useState("");
  const [standardsInput, setStandardsInput] = useState("");
  const [errors, setErrors] = useState<{ name?: string; type?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = t("schema.createDialog.nameRequired");
    if (!type) next.type = t("schema.createDialog.typeRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const standardsCovered = standardsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/schemas", {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        displayVersion: displayVersion.trim() || undefined,
        standardsCovered: standardsCovered.length > 0 ? standardsCovered : undefined,
      });
      toast.success(t("schema.createDialog.successTitle"), `"${name}" has been created as a draft.`);
      setName("");
      setDescription("");
      setType("");
      setDisplayVersion("");
      setStandardsInput("");
      setErrors({});
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(
        t("schema.createDialog.failTitle"),
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setName("");
    setDescription("");
    setType("");
    setDisplayVersion("");
    setStandardsInput("");
    setErrors({});
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("schema.createDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("schema.createDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputGroup
            label={t("schema.schemaName")}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("schema.createDialog.namePlaceholder")}
            errorText={errors.name}
          />

          <div className="flex flex-col gap-[6px]">
            <label className="text-[14px] font-medium text-text-secondary">
              {t("schema.type")} <span className="text-danger">*</span>
            </label>
            <SelectRoot
              value={type}
              onValueChange={(v) => setType(v as SchemaType)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("schema.createDialog.selectType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SINGLE_STANDARD">{t("schema.types.SINGLE_STANDARD")}</SelectItem>
                <SelectItem value="MULTI_STANDARD">{t("schema.types.MULTI_STANDARD")}</SelectItem>
                <SelectItem value="INTERNAL_POLICIES">{t("schema.types.INTERNAL_POLICIES")}</SelectItem>
                <SelectItem value="CUSTOM">{t("schema.types.CUSTOM")}</SelectItem>
              </SelectContent>
            </SelectRoot>
            {errors.type && (
              <p className="text-[12px] text-danger">{errors.type}</p>
            )}
          </div>

          <InputGroup
            label={t("schema.version")}
            value={displayVersion}
            onChange={(e) => setDisplayVersion(e.target.value)}
            placeholder="e.g. 1.0"
          />

          <InputGroup
            label={t("schema.standards")}
            value={standardsInput}
            onChange={(e) => setStandardsInput(e.target.value)}
            placeholder="e.g. ISO 27001, DORA, NIS2"
            helperText={t("schema.standardsHint")}
          />

          <TextareaGroup
            label={t("entity.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("schema.createDialog.descriptionPlaceholder")}
            rows={3}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {t("schema.newSchema")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Copy Schema Dialog ────────────────────────

interface CopySchemaDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sourceSchema: Schema | null;
  onCopied: (newSchemaId: string) => void;
}

function CopySchemaDialog({ open, onOpenChange, sourceSchema, onCopied }: CopySchemaDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Pre-fill name when source changes
  useEffect(() => {
    if (sourceSchema) setName(`${sourceSchema.name} (Copy)`);
  }, [sourceSchema]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!sourceSchema) return;
    if (!name.trim()) { setError("Name is required"); return; }
    setIsSubmitting(true);
    try {
      const res = await api.post<{ data: { id: string } }>(`/schemas/${sourceSchema.id}/copy`, {
        name: name.trim(),
      });
      toast.success("Schema copied", `"${name}" has been created as a draft.`);
      setName("");
      setError(undefined);
      onOpenChange(false);
      onCopied(res.data?.id ?? "");
    } catch (err) {
      toast.error("Failed to copy", err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setName("");
    setError(undefined);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate Schema</DialogTitle>
          <DialogDescription>
            Create a copy of "{sourceSchema?.name}". The copy will start as a DRAFT.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <InputGroup
            label="New Schema Name"
            required
            value={name}
            onChange={(e) => { setName(e.target.value); setError(undefined); }}
            placeholder="e.g. My Custom DORA"
            errorText={error}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              Duplicate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────

export function SchemasList() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SchemaVersionStatus | "">("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Schema | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copyTarget, setCopyTarget] = useState<Schema | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const fetchSchemas = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter) params.set("status", statusFilter);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));

      const res = await api.get<SchemasResponse>(`/schemas?${params.toString()}`);
      setSchemas(res.data ?? []);
      setTotal(res.total ?? 0);
      setIsError(false);
    } catch {
      // Distinguish a real failure from a genuinely empty result so the UI can
      // show an error/retry state instead of the "no schemas created" empty state.
      setSchemas([]);
      setTotal(0);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, statusFilter, page]);

  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/schemas/${deleteTarget.id}`);
      toast.success(t("schema.deleteConfirm.success"), `"${deleteTarget.name}" has been deleted.`);
      setDeleteTarget(null);
      fetchSchemas();
    } catch (err) {
      toast.error(
        t("schema.deleteConfirm.fail"),
        err instanceof Error ? err.message : "Unknown error"
      );
    } finally {
      setIsDeleting(false);
    }
  }

  // Find the active (published) schema
  const activeSchema = schemas.find((s) => s.publishedVersion !== null && s.publishedVersion !== undefined);

  const columns = buildColumns(navigate, (schema) => setDeleteTarget(schema), (schema) => setCopyTarget(schema), t, language);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("schema.title")}
          </h1>
          <p className="text-body text-text-secondary">
            {t("schema.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          {t("schema.newSchema")}
        </Button>
      </div>

      {/* Active schema card */}
      {activeSchema && (
        <ActiveSchemaCard
          schema={activeSchema}
          onView={() =>
            navigate(
              `/schemas/${activeSchema.id}/versions/${activeSchema.publishedVersion!.id}`
            )
          }
        />
      )}

      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="max-w-xs flex-1">
          <Input
            leadingIcon={<Search className="h-4 w-4" />}
            placeholder={t("schema.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <SelectRoot
          value={statusFilter || "ALL"}
          onValueChange={(v) => {
            setStatusFilter((v === "ALL" ? "" : v) as SchemaVersionStatus | "");
            setPage(1);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("common.allStatuses")}</SelectItem>
            <SelectItem value="DRAFT">{t("status.draft")}</SelectItem>
            <SelectItem value="IN_REVIEW">{t("status.inReview")}</SelectItem>
            <SelectItem value="PUBLISHED">{t("status.published")}</SelectItem>
            <SelectItem value="DEPRECATED">{t("status.deprecated")}</SelectItem>
          </SelectContent>
        </SelectRoot>
      </div>

      {/* Table — distinguish a load error from a genuinely empty list */}
      {isError && !isLoading ? (
        <div className="rounded-card border border-border-default bg-card">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load schemas"
            description="Something went wrong while loading schemas. Please try again."
            action={{ label: "Retry", onClick: () => { fetchSchemas(); } }}
          />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={schemas}
          isLoading={isLoading}
          onRowClick={(row) => navigate(`/schemas/${row.id}`)}
          emptyState={{
            icon: Braces,
            title: t("schema.noSchemasCreated"),
            description: t("schema.noSchemasCreatedDesc"),
            action: {
              label: `+ ${t("schema.newSchema")}`,
              onClick: () => setCreateOpen(true),
            },
          }}
          page={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      {/* Create dialog */}
      <CreateSchemaDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={fetchSchemas}
      />

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
          title={t("schema.deleteConfirm.title")}
          description={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          confirmLabel={t("common.delete")}
          isDestructive
          isLoading={isDeleting}
          onConfirm={handleDelete}
        />
      )}

      {/* Copy dialog */}
      <CopySchemaDialog
        open={Boolean(copyTarget)}
        onOpenChange={(v) => { if (!v) setCopyTarget(null); }}
        sourceSchema={copyTarget}
        onCopied={(newId) => {
          setCopyTarget(null);
          fetchSchemas();
          if (newId) navigate(`/schemas/${newId}`);
        }}
      />
    </>
  );
}
